import { useEffect, useRef, useState } from "react";
import { useQuerySuggestions } from "../../../hooks/usePolicy";
import { Suggestion } from "@repo/api/schemas/policies.schema";
import { useIntl } from "react-intl";
import { usePoliciesForm } from "../PoliciesFormContext";

export default function CodeEditor() {
  const intl = useIntl();
  const { evaluationCriteria, setEvaluationCriteria } = usePoliciesForm();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showBox, setShowBox] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cursor, setCursor] = useState({ top: 0, left: 0 });

  const [queryParams, setQueryParams] = useState<{
    text: string;
    cursor_pos: number;
  } | null>(null);

  const { data: suggestionData } = useQuerySuggestions(
    queryParams ?? { text: "", cursor_pos: 0 },
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const hasInitialFocused = useRef(false);

  const lineCount = evaluationCriteria.split("\n").length || 1;

  // ---------------- SCROLL SYNC ----------------
  const handleScroll = () => {
    if (!textareaRef.current || !lineRef.current) return;
    lineRef.current.scrollTop = textareaRef.current.scrollTop;
  };

  // ---------------- CARET POSITION ----------------
  const getCaretCoordinates = (textarea: HTMLTextAreaElement) => {
    const { selectionStart } = textarea;
    const textBefore = textarea.value.substring(0, selectionStart);
    const lines = textBefore.split("\n");

    return {
      top: lines.length * 24 - textarea.scrollTop,
      left: lines[lines.length - 1].length * 8 + 60,
    };
  };

  useEffect(() => {
    if (!Array.isArray(suggestionData?.suggestions)) return;
    setSuggestions(suggestionData.suggestions);
    setShowBox(suggestionData.suggestions.length > 0);
    setActiveIndex(0);
  }, [suggestionData]);

  // ---------------- API CALL ----------------
  const fetchAutocomplete = (text: string, cursorPos: number) => {
    setQueryParams({ text, cursor_pos: cursorPos });
  };

  // ---------------- TRIGGER AUTOCOMPLETE ----------------
  const triggerAutocomplete = (value: string) => {
    const textarea = textareaRef.current!;
    const cursorPos = textarea.selectionStart;

    const coords = getCaretCoordinates(textarea);
    setCursor(coords);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAutocomplete(value, cursorPos);
    }, 300);
  };

  // ---------------- INSERT SUGGESTION ----------------
  const insertSuggestion = (suggestion: Suggestion) => {
    const textarea = textareaRef.current!;
    const start = textarea.selectionStart;
    const before = evaluationCriteria.substring(0, start);
    const after = evaluationCriteria.substring(start);

    const newText = before + suggestion.text + after;
    setEvaluationCriteria(newText);
    setShowBox(false);

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd =
        start + suggestion.text.length;
    }, 0);
  };

  // ---------------- KEYBOARD HANDLING ----------------
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showBox || !suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev === 0 ? suggestions.length - 1 : prev - 1,
      );
    }

    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertSuggestion(suggestions[activeIndex]);
    }

    if (e.key === "Escape") {
      setShowBox(false);
    }
  };

  // ---------------- FOCUS HANDLER ----------------
  const handleFocus = () => {
    if (hasInitialFocused.current && evaluationCriteria) return;
    hasInitialFocused.current = true;
    const textarea = textareaRef.current;
    if (!textarea) return;
    // TODO: remove the empty string with space once the API can handle empty text
    fetchAutocomplete(evaluationCriteria || " ", textarea.selectionStart);
  };

  // ---------------- CHANGE HANDLER ----------------
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setEvaluationCriteria(value);
    triggerAutocomplete(value);
  };

  // ---------------- UI ----------------
  return (
    <div className="relative w-full h-64 border rounded-md overflow-hidden">
      <div className="flex h-full">
        {/* Line Numbers */}
        <div
          ref={lineRef}
          className="px-3 py-2 text-right select-none overflow-hidden"
          style={{
            background: "var(--color-background-neutral-subtle)",
            color: "var(--color-foreground-neutral-medium)",
          }}
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i} className="leading-6">
              {i + 1}
            </div>
          ))}
        </div>

        {/* Editor */}
        <textarea
          ref={textareaRef}
          value={evaluationCriteria}
          spellCheck={false}
          onChange={handleChange}
          onFocus={handleFocus}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({
            defaultMessage:
              "// Write your policy code here\n// e.g.  let rs1 = human_identity where metadata.status == 'Terminated'",
            id: "MNaejC",
            description: "Placeholder for IQL code editor",
          })}
          className="Policies-Eval-Criteria flex-1 resize-none outline-none p-2 leading-6"
          style={{
            background: "var(--color-background-neutral-subtlest)",
            color: "var(--color-foreground-neutral-bold)",
          }}
        />
      </div>

      {/* AUTOCOMPLETE BOX */}
      {showBox && suggestions.length > 0 && (
        <div
          style={{
            top: cursor.top,
            left: cursor.left,
            background: "var(--color-background-neutral-subtlest)",
            color: "var(--color-foreground-neutral-bold)",
          }}
          className="absolute border rounded-md shadow-md w-72 z-50 max-h-48 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <div
              key={i}
              onMouseDown={() => insertSuggestion(s)}
              className="px-3 py-2 cursor-pointer"
              style={
                i === activeIndex
                  ? {
                      background:
                        "var(--color-background-primary-on-subtle-selected)",
                    }
                  : undefined
              }
            >
              {s.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
